"use client";

import * as React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useIsAuthPage } from "@/hooks/use-is-auth-page";
import {
  createSequenceListener,
  KEYBOARD_SHORTCUTS,
  matchesShortcut,
  type ShortcutId,
} from "@/lib/keyboard-shortcuts";

export type CommandBarPage = "root" | "customers";

type CommandBarContextType = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Page shown when the bar opens; reset to "root" by plain open/toggle. */
  initialPage: CommandBarPage;
  openAt: (page: CommandBarPage) => void;
};

const SEQUENCE_PAGES = {
  "global-switch-customer": "customers",
} as const satisfies Partial<Record<ShortcutId, CommandBarPage>>;

const CommandBarContext = createContext<CommandBarContextType | undefined>(undefined);

export function CommandBarProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpenState] = useState(false);
  const [initialPage, setInitialPage] = useState<CommandBarPage>("root");
  const setIsOpen = React.useCallback((open: boolean) => {
    if (open) setInitialPage("root");
    setIsOpenState(open);
  }, []);
  const openAt = React.useCallback((page: CommandBarPage) => {
    setInitialPage(page);
    setIsOpenState(true);
  }, []);
  const isAuthPage = useIsAuthPage();

  // Handle keyboard shortcut (Cmd+K or Ctrl+K). Disabled on auth pages —
  // same routes where the sidebar is hidden — and closed if still open when
  // navigating onto one.
  useEffect(() => {
    if (isAuthPage) {
      setIsOpenState(false);
      return;
    }

    const onSequence = createSequenceListener(
      Object.keys(SEQUENCE_PAGES) as (keyof typeof SEQUENCE_PAGES)[],
      (id) => openAt(SEQUENCE_PAGES[id]),
    );

    const down = (e: KeyboardEvent) => {
      if (matchesShortcut(e, KEYBOARD_SHORTCUTS["global-command-bar"])) {
        e.preventDefault();
        setInitialPage("root");
        setIsOpenState((open) => !open);
        return;
      }
      if (!document.querySelector("[role=dialog]")) onSequence(e);
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isAuthPage, openAt]);

  return React.createElement(
    CommandBarContext.Provider,
    { value: { isOpen, setIsOpen, initialPage, openAt } },
    children,
  );
}

export function useCommandBar() {
  const context = useContext(CommandBarContext);

  if (context === undefined) {
    throw new Error("useCommandBar must be used within a CommandBarProvider");
  }

  return context;
}
